<template>
  <div>
    <div id="call-records-view" class="page-table scrollable">
      <el-dialog
        width="440px"
        :visible="modalVisible"
        :before-close="handleCloseModal"
        :close-on-click-modal="false"
      >
        <h2 class="form-section-header">Filter Settings</h2>

        <h3 class="black-text mb-5">Select Date Range</h3>
        <el-date-picker
          class="mb-10"
          style="margin-left: auto"
          v-model="historyDateRange"
          type="datetimerange"
          align="right"
          unlink-panels
          format="yyyy-MM-dd HH:mm"
          :clearable="false"
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          :picker-options="datePickerOptions"
        >
        </el-date-picker>

        <el-form id="stats-advanced-modal" class="form-modal">
          <dads-select
            v-model="userId"
            filterable
            clearable
            placeholder="Select User"
            remote
            :remote-method="remoteSearchUsers"
          >
            <template slot="prefix">Select User</template>
            <template v-for="user in userOptions">
              <el-option
                v-if="user.userId"
                :key="user.userId"
                :label="`${user.name} - ${user.number}`"
                :value="user.userId"
              />
            </template>
          </dads-select>

          <h2 class="form-section-header">Select Events</h2>
          <dads-select
            v-model="events"
            placeholder="Events"
            multiple
            filterable
            clearable
          >
            <div v-for="{ label, value } in eventOptions" :key="label">
              <el-option :label="label" :value="value" />
            </div>
          </dads-select>

          <el-button class="mt-20" @click="doAdvanced"> Update </el-button>
        </el-form>
      </el-dialog>

      <div id="users-stats-view" class="no-table-box page-header">
        <div class="flex justify-space-between mb-20">
          <h2 class="mb-0 mt-0 text-left">
            <v-svg sprite="profile" class="voxo-aqua"></v-svg>
            User Logs
          </h2>
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px"></i>
            Total: {{ total }}
          </el-tag>
        </div>

        <div class="flex pb-20">
          <div class="box">
            <el-radio-group
              v-model="presetSelection"
              @change="doPreset"
              class="hidden-xs-only"
            >
              <el-radio-button type="default" label="yesterday"
                >Yesterday</el-radio-button
              >
              <el-radio-button type="default" label="last10"
                >Last 10 Days</el-radio-button
              >
              <el-radio-button label="lastMonth">Last 30 Days</el-radio-button>
            </el-radio-group>

            <el-tooltip placement="top" :content="selectedDateRange">
              <el-button @click="modalVisible = true" class="ml-10">
                Filter
              </el-button>
            </el-tooltip>
            <el-button
              @click="resetTable"
              v-if="advancedActive"
              style="background-color: #f25a6b; color: #fff"
            >
              Reset
            </el-button>
            <el-button class="ml-10" @click="doExport"> Export </el-button>
          </div>

          <div class="box right" v-if="pages > 1">
            <el-pagination
              small
              background
              layout="prev, pager, next"
              @current-change="pageSet"
              @next-click="pageNext"
              @prev-click="pagePrevious"
              :current-page="page"
              :page-size="limit"
              :pager-count="5"
              :total="total"
            />
          </div>
        </div>

        <div class="vxe-table-height card-base card-shadow--medium">
          <vxe-grid
            round
            class="user-stats-table"
            border="inner"
            ref="xGrid"
            height="100%"
            auto-resize
            show-overflow="tooltip"
            empty-text="No Data"
            :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
            :data="tableData"
            :loading="tableLoading"
            :columns="tableColumns"
          >
          </vxe-grid>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import feathersClient from "@/feathers/feathers-client";
import debounce from "lodash/debounce";
import moment from "moment-timezone";
import { ExportToCsv } from "export-to-csv";
import DadsSelect from "@/components/select";

export default {
  name: "UserLogs",

  components: {
    DadsSelect,
  },

  data() {
    return {
      tableLoading: false,
      modalVisible: false,
      tableData: [],
      presetSelection: "last10",
      advancedActive: false,
      exportCSV: false,
      page: 1,
      pages: 0,
      limit: 150,
      total: 0,

      eventOptions: [
        {
          label: "Normal Login",
          value: "Login",
        },
        {
          label: "App Connect",
          value: "App Connect",
        },
        {
          label: "User Logout",
          value: "Logout",
        },
        {
          label: "Enable DND",
          value: "Enable DND",
        },
        {
          label: "Disabled DND",
          value: "Disabled DND",
        },
        {
          label: "Queue Login",
          value: "Queue Login",
        },
        {
          label: "Queue Logout",
          value: "Queue Logout",
        },
        {
          label: "Queue Pause",
          value: "Queue Pause",
        },
        {
          label: "Queue Unpause",
          value: "Queue Unpause",
        },
        {
          label: "Enabled Softphone",
          value: "Enabled Softphone",
        },
        {
          label: "Disabled Softphone",
          value: "Disabled Softphone",
        },
        {
          label: "Outbound Call",
          value: "Outbound Call",
        },
        {
          label: "Accept Call",
          value: "Accept Call",
        },
        {
          label: "Reject Call",
          value: "Reject Call",
        },
        {
          label: "Network Test",
          value: "Network Test",
        },
        {
          label: "Update Timezone",
          value: "Update Timezone",
        },
        {
          label: "Call Notifications Enabled",
          value: "Enable Call Push Notifications",
        },
        {
          label: "Call Notifications Disabled",
          value: "Disable Call Push Notifications",
        },
        {
          label: "Enable Extension Forwarding",
          value: "Enable Extension Forwarding",
        },
        {
          label: "Disable Extension Forwarding",
          value: "Disable Extension Forwarding",
        },
        {
          label: "Password Reset",
          value: "Password Reset",
        },
        {
          label: "Forgot Password",
          value: "Forgot Password",
        },
        {
          label: "Coach",
          value: "Coach",
        },
        {
          label: "Listen",
          value: "Listen",
        },
        {
          label: "Admin Disable DND",
          value: "Admin Disable DND",
        },
        {
          label: "Admin Queue Login",
          value: "Admin Queue Login",
        },
        {
          label: "Admin Queue Logout",
          value: "Admin Queue Logout",
        },
        {
          label: "Admin Queue Pause",
          value: "Admin Queue Pause",
        },
        {
          label: "Admin Queue Unpause",
          value: "Admin Queue Unpause",
        },
        {
          label: "Send Fax",
          value: "Sent Fax",
        },
        {
          label: "Queue Withdraw",
          value: "Withdraw"
        }
      ],

      userId: "",
      userQuery: "",
      userOptions: [],
      events: [],
      startDate: null,
      endDate: null,
      historyDateRange: [moment(), moment()],
      datePickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "week").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(1, "month").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(3, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 6 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(6, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last year",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(12, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },

      // ⭐️ Table Columns ⭐️ //
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-aqua" sprite="profile" />
                </div>,
              ];
            },
          },
        },
        {
          field: "eventTime",
          title: "Event Time",
          width: 150,
          showOverflow: true,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [
                <span class="first-table-column">
                  {this.timeFormatter(row.eventTime)}
                </span>,
              ];
            },
          },
        },
        {
          field: "userEmail",
          title: "Email",
          width: 200,
          showOverflow: true,
        },
        {
          field: "extName",
          title: "Extension Name",
          width: 150,
          showOverflow: true,
          sortable: true,
        },
        {
          field: "peerName",
          title: "Extension",
          width: 150,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [
                <span>{row.peerName ? row.peerName.split("-")[0] : ""}</span>,
              ];
            },
          },
        },
        {
          field: "event",
          title: "Event",
          minWidth: 150,
          showOverflow: true,
          sortable: true,
        },
        {
          field: "eventDescription",
          title: "Event Description",
          minWidth: 200,
          sortable: true,
          showOverflow: true,
        },
      ],
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    userOffsetMinutes() {
      return this.$store.state.userTimeOffset;
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    selectedDateRange() {
      if (this.endDate && this.startDate) {
        const dateStart = moment(this.startDate).format("M/D/YY HH:mm");
        const dateEnd = moment(this.endDate).format("M/D/YY HH:mm");
        return `${dateStart} - ${dateEnd}`;
      }
    },
  },

  watch: {
    page: debounce(function () {
      this.queryStats();
    }, 500),

    selectedTenant() {
      this.resetTable();
    },

    userQuery: debounce(function () {
      this.queryUsers();
    }, 500),
  },

  methods: {
    queryUsers() {
      const params = {
        query: {
          tenantId: this.selectedTenant.id,
          $limit: 25,
        },
      };

      if (this.userQuery && this.userQuery.length) {
        params.query.$or = {
          name: { $like: `%${this.userQuery}%` },
          number: { $like: `%${this.userQuery}%` },
        };
      }

      feathersClient
        .service("extensions")
        .find(params)
        .then((res) => {
          this.userOptions = [...res];
        });
    },

    remoteSearchUsers(query) {
      this.userQuery = query;
    },

    pageNext() {
      if (this.page < this.pages) {
        this.page += 1;
      }
    },
    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },
    pageSet(page) {
      this.page = page;
    },

    handleCloseModal() {
      this.modalVisible = false;
    },

    doPreset(val) {
      if (val === "init") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment().startOf("day").format("YYYY-MM-DD");
        this.presetSelection = "init";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "last10") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment().subtract(9, "days").format("YYYY-MM-DD");
        this.presetSelection = "last10";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "lastMonth") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment().subtract(30, "days").format("YYYY-MM-DD");
        this.presetSelection = "lastMonth";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "yesterday") {
        this.endDate = moment()
          .startOf("day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment()
          .subtract(1, "day")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm");
        this.presetSelection = "yesterday";
        this.updateDateRangePicker();
        this.queryStats();
      }
    },

    doAdvanced() {
      this.advancedActive = true;
      this.startDate = moment(this.historyDateRange[0]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.endDate = moment(this.historyDateRange[1]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.updateDateRangePicker();
      this.queryStats();
      this.handleCloseModal();
      this.presetSelection = null;
    },

    resetTable() {
      this.advancedActive = false;
      this.userId = "";
      this.events = [];
      this.exportCSV = false;
      this.page = 1;
      this.doPreset("init");
    },

    queryStats() {
      this.tableLoading = true;
      feathersClient
        .service("user-events")
        .find({
          query: {
            startDate: this.startDate,
            endDate: this.endDate,
            userId: this.userId,
            events: this.events,
            timezone: this.user.timezone,
            tenantId: this.selectedTenant.id,
            $limit: this.limit,
            $skip: this.page > 1 ? this.limit * (this.page - 1) : 0,
            exportCSV: this.exportCSV,
          },
        })
        .then((res) => {
          //update table if not exporting
          if (!this.exportCSV && res && res.data) {
            this.total = res.total;
            this.tableData = res.data;
            this.pages = Math.ceil(res.total / this.limit);
            this.page = this.page > this.pages ? 1 : this.page;
          }
          this.exportCSV = false;
          this.tableLoading = false;
        })
        .catch(() => {
          this.tableData = [];
          this.tableLoading = false;
        });
    },

    updateDateRangePicker() {
      this.historyDateRange = [moment(this.startDate), moment(this.endDate)];
    },

    timeFormatterExport(value) {
      const offsetMinutes = Math.abs(this.userOffsetMinutes);

      return this.userOffsetMinutes < 0
        ? moment(value)
            .subtract(offsetMinutes, "minutes")
            .format("MM-DD-YYYY hh:mm:ss A")
        : moment(value)
            .add(offsetMinutes, "minutes")
            .format("MM-DD-YYYY hh:mm:ss A");
    },

    timeFormatter(value) {
      const offsetMinutes = Math.abs(this.userOffsetMinutes);

      return this.userOffsetMinutes < 0
        ? moment(value)
            .subtract(offsetMinutes, "minutes")
            .format("M/DD/YY h:mm A")
        : moment(value).add(offsetMinutes, "minutes").format("M/DD/YY h:mm A");
    },

    doExport() {
      this.$confirm(`Export data for ${this.total} logs?`, {
        type: "warning",
      })
        .then(() => {
          if (this.pages === 1) {
            //export from browser
            let csvData = [];
            for (let i = 0; i < this.tableData.length; i++) {
              const csvObj = {};
              let exclusionList = ["_XID"];
              for (let key in this.tableData[i]) {
                if (exclusionList.includes(key) === false) {
                  csvObj[key] = this.tableData[i][key]
                    ? this.tableData[i][key]
                    : "";
                }
              }
              csvData.push(csvObj);
            }

            const options = {
              filename: "user_event_logs",
              showLabels: true,
              headers: [
                "Event Time",
                "Extension Name",
                "Peer Name",
                "Event",
                "Event Description",
              ],
            };

            let csvRecorder = csvData.map((obj) => {
              let reorderObj = {};
              reorderObj["eventTime"] = this.timeFormatterExport(obj.eventTime);
              reorderObj["extName"] = obj.extName;
              reorderObj["peerName"] = obj.peerName;
              reorderObj["event"] = obj.event;
              reorderObj["eventDescription"] = obj.eventDescription;

              return reorderObj;
            });

            csvData.push(csvRecorder);
            const csvExporter = new ExportToCsv(options);
            csvExporter.generateCsv(csvRecorder);
          } else {
            this.exportCSV = true;
            this.queryUserEventLogs();
          }
        })
        .catch(() => true);
    },

    downloadCSV(data) {
      const { url, downloadName } = data;
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadName;
      link.click();
    },

    queryUserEventLogs() {
      this.tableLoading = true;

      feathersClient
        .service("user-events")
        .find({
          query: {
            startDate: this.startDate,
            endDate: this.endDate,
            userId: this.userId,
            events: this.events,
            timezone: this.user.timezone,
            tenantId: this.selectedTenant.id,
            $limit: this.limit,
            $skip: this.page > 1 ? this.limit * (this.page - 1) : 0,
            exportCSV: this.exportCSV,
          },
        })
        .then((res) => {
          if (!this.exportCSV) {
            this.limit = res.limit;
            this.pages = Math.ceil(res.total / res.limit);
            this.total = res.total;
            this.page = this.page > this.pages ? 1 : this.page;
            this.tableData = res.data;
          }

          if (this.exportCSV) {
            if (!res.url) {
              this.$notify({
                title: "Not Found",
                message: "File Not Found.",
                type: "warning",
              });
            } else {
              this.downloadCSV(res);
            }
          }

          this.exportCSV = false;
          this.tableLoading = false;
        })
        .catch(() => {
          this.tableLoading = false;
        });
    },
  },

  mounted() {
    this.doPreset("init");
    this.queryUsers();
  },
};
</script>
